/**
 * Hook to load/persist user enrichment layers from Supabase.
 * Hydrates the compatibility store on mount so layer data survives sessions.
 */

import { useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useCompatibilityStore } from '@/stores/compatibilityStore';
import { logger } from '@/utils/structuredLogging';
import type { DataLayer } from '@/types/compatibility';

export type EnrichmentStatus = 'empty' | 'partial' | 'complete';

export interface EnrichmentLayerRow {
  id: string;
  user_id: string;
  layer: string;
  data: Record<string, unknown>;
  status: EnrichmentStatus;
  version: number;
  created_at: string;
  updated_at: string;
}

const LAYER_TO_PROFILE_FIELD: Record<string, string> = {
  demographics: 'demographics',
  attachment: 'attachment',
  values: 'values',
  loveLanguages: 'loveLanguages',
  lifestyle: 'lifestyle',
  narrative: 'narrative',
  checkIn: 'checkIn',
  communication: 'communication',
  sexualIntimacy: 'sexualIntimacy',
};

export function useEnrichmentLayers() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['enrichment-layers', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('user_enrichment_layers')
        .select('*')
        .eq('user_id', user.id);
      if (error) throw error;
      return (data || []) as EnrichmentLayerRow[];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Hydrate the compatibility store from persisted layers on load
  useEffect(() => {
    if (!query.data || query.data.length === 0) return;

    const store = useCompatibilityStore.getState();
    // Only hydrate personA (the current user)
    for (const row of query.data) {
      const layer = row.layer as DataLayer;
      if (LAYER_TO_PROFILE_FIELD[layer] && row.status !== 'empty') {
        store.updatePersonLayer('A', layer, row.data);
      }
    }
  }, [query.data]);

  // Realtime subscription for cross-device enrichment layer sync
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`enrichment-layers-${user.id}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_enrichment_layers',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['enrichment-layers', user.id] });
          logger.info('Enrichment layers updated via realtime', { component: 'useEnrichmentLayers' });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, queryClient]);

  const upsertLayer = useMutation({
    mutationFn: async (params: { layer: DataLayer; data: Record<string, unknown>; status: EnrichmentStatus }) => {
      if (!user?.id) throw new Error('Not authenticated');
      
      // Optimistic concurrency: read current version, let DB trigger increment
      const { data: existing } = await supabase
        .from('user_enrichment_layers')
        .select('version')
        .eq('user_id', user.id)
        .eq('layer', params.layer as any)
        .maybeSingle();
      
      const currentVersion = (existing as any)?.version ?? 0;
      
      const { data, error } = await supabase
        .from('user_enrichment_layers')
        .upsert([{
          user_id: user.id,
          layer: params.layer as any,
          data: params.data as any,
          status: params.status as any,
          version: currentVersion, // DB trigger will auto-increment on update
        }], { onConflict: 'user_id,layer' })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrichment-layers', user?.id] });
    },
  });

  const persistLayer = useCallback(async (layer: DataLayer, data: Record<string, unknown>, status: EnrichmentStatus = 'complete') => {
    return upsertLayer.mutateAsync({ layer, data, status });
  }, [upsertLayer]);

  return {
    layers: query.data || [],
    loading: query.isLoading,
    persistLayer,
    isPersisting: upsertLayer.isPending,
    getLayer: (layer: DataLayer) => (query.data || []).find(r => r.layer === layer),
  };
}
